import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";

export interface UseTileInsertOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

export const useTileInsert = (options?: UseTileInsertOptions) => {
  const [isInserting, setIsInserting] = useState(false);
  const [insertingTileId, setInsertingTileId] = useState<string | null>(null);

  const formatTileText = (tile: ReviewTileItem): string => {
    let text = "";

    // Add clause/section header
    if (tile.clause) {
      text += `**${tile.clause}**\n\n`;
    }

    // Add content based on available fields
    if (tile.reason) {
      text += `**Reason:** ${tile.reason}\n\n`;
    }

    if (tile.analysis) {
      text += `**Analysis:** ${tile.analysis}\n\n`;
    }

    if (tile.explanation) {
      text += `**Explanation:** ${tile.explanation}\n\n`;
    }

    return text.trim();
  };

  const extractSuggestedClause = (tile: ReviewTileItem): string | null => {
    const originalData = tile.originalData;
    if (!originalData) return null;

    // Check for various suggested clause field names based on tile type
    return (
      originalData.suggestedclause ||
      originalData.suggestion ||
      originalData.suggestions ||
      originalData.suggestedNumber ||
      originalData.additionalClause ||
      originalData.missingClause ||
      null
    );
  };

  const insertSingleTile = async (tile: ReviewTileItem): Promise<boolean> => {
    setIsInserting(true);
    setInsertingTileId(tile.id);

    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load(["text"]);
        await context.sync();

        const hasSelectedText = selection.text.length > 0;
        const suggestedClause = extractSuggestedClause(tile);

        let textToInsert: string;
        let insertLocation: Word.InsertLocation;
        let successMessage: string;

        if (suggestedClause) {
          textToInsert = suggestedClause;
          if (hasSelectedText) {
            insertLocation = Word.InsertLocation.replace;
            successMessage = `Selected text replaced with suggested clause.`;
          } else {
            insertLocation = Word.InsertLocation.end;
            successMessage = `Suggested clause inserted.`;
          }
        } else {
          // Fall back to inserting formatted tile content
          textToInsert = formatTileText(tile) + "\n\n";
          insertLocation = Word.InsertLocation.end;
          successMessage = `Tile "${tile.clause || tile.id}" inserted successfully`;
        }

        selection.insertText(textToInsert, insertLocation);
        await context.sync();

        options?.onSuccess?.(successMessage);
      });
      return true;
    } catch (error: any) {
      console.error("Error inserting tile:", error);
      options?.onError?.(`Failed to insert tile: ${error.message}`);
      return false;
    } finally {
      setIsInserting(false);
      setInsertingTileId(null);
    }
  };

  const insertMultipleTiles = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to insert");
      return false;
    }

    setIsInserting(true);

    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load(["text"]);
        await context.sync();

        const hasSelectedText = selection.text.length > 0;

        if (hasSelectedText) {
          // For bulk operations with selected text, we'll insert the first tile's suggested clause
          // if available, or use formatted content for the first tile
          const firstTile = tiles[0];
          if (!firstTile) {
            throw new Error("No tiles available for insertion");
          }
          const suggestedClause = extractSuggestedClause(firstTile);

          if (suggestedClause) {
            selection.insertText(suggestedClause, Word.InsertLocation.replace);

            // Insert remaining tiles after the replaced content
            if (tiles.length > 1) {
              const remainingTilesText = tiles
                .slice(1)
                .map((tile) => formatTileText(tile))
                .join("\n\n---\n\n");
              selection.insertText(
                "\n\n---\n\n" + remainingTilesText + "\n\n",
                Word.InsertLocation.end
              );
            }

            options?.onSuccess?.(
              `Selected text replaced with suggested clause. ${tiles.length} tiles processed.`
            );
          } else {
            // No suggested clause, fall back to formatted content
            const allTilesText = tiles.map((tile) => formatTileText(tile)).join("\n\n---\n\n");
            selection.insertText(allTilesText + "\n\n", Word.InsertLocation.end);
            options?.onSuccess?.(`${tiles.length} tiles inserted successfully`);
          }
        } else {
          // No text selected, use standard bulk insertion
          const allTilesText = tiles.map((tile) => formatTileText(tile)).join("\n\n---\n\n");
          selection.insertText(allTilesText + "\n\n", Word.InsertLocation.end);
          options?.onSuccess?.(`${tiles.length} tiles inserted successfully`);
        }

        await context.sync();
      });
      return true;
    } catch (error: any) {
      console.error("Error inserting tiles:", error);
      options?.onError?.(`Failed to insert tiles: ${error.message}`);
      return false;
    } finally {
      setIsInserting(false);
    }
  };

  return {
    isInserting,
    insertingTileId,
    insertSingleTile,
    insertMultipleTiles,
  };
};
